import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { OrderService, OrderDto, OrderItemDto } from '@proxy/orders';
import { WineService, WineDto } from '@proxy/wines';
import { APP_ICONS } from '../../shared/icons/app-icons';
import { StorageService } from '../../shared/services/storage.service';

interface ExtendedOrderItemDto extends OrderItemDto {
  wine?: WineDto;
}

interface ExtendedOrderDto extends OrderDto {
  orderItems: ExtendedOrderItemDto[];
}

@Component({
  selector: 'app-assisted-order-details',
  standalone: false,
  templateUrl: './assisted-order-details.component.html'
})
export class AssistedOrderDetailsComponent implements OnInit {
  order: ExtendedOrderDto | null = null;
  isLoading = true;
  error: string | null = null;
  iconBack = APP_ICONS.arrow;

  constructor(
    private route: ActivatedRoute,
    private orderService: OrderService,
    private wineService: WineService,
    private storageService: StorageService,
    private location: Location
  ) {}

  ngOnInit(): void {
    const orderIdParam = this.route.snapshot.paramMap.get('orderId');

    if (orderIdParam) {
      const orderId = +orderIdParam;
      if (!isNaN(orderId)) {
        this.loadOrderDetails(orderId);
      } else {
        this.error = 'ID de orden inválido';
        this.isLoading = false;
      }
    } else {
      this.error = 'No se proporcionó ID de orden';
      this.isLoading = false;
    }
  }

  loadOrderDetails(orderId: number): void {
    this.orderService.get(orderId).subscribe({
      next: (result) => {
        this.order = result as ExtendedOrderDto;
        if (this.order.orderItems && this.order.orderItems.length > 0) {
          this.loadWineDetails();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Error al cargar los detalles de la orden:', err);
        if (err.status === 403 || err.status === 404) {
          this.error = 'No tienes permiso para ver esta orden o la orden no existe';
        } else {
          this.error = 'No se pudo cargar la información de la orden';
        }
        this.isLoading = false;
      }
    });
  }

  loadWineDetails(): void {
    if (!this.order) return;

    const wineIds = this.order.orderItems
      .map(item => item.wineId)
      .filter(id => id !== undefined) as number[];

    if (wineIds.length === 0) {
      this.isLoading = false;
      return;
    }

    const wineRequests = wineIds.map(wineId =>
      this.wineService.getIncludingDeleted(wineId).toPromise()
    );

    Promise.all(wineRequests).then(wineResults => {
      this.order!.orderItems.forEach(item => {
        const wineDetail = wineResults.find(wine => wine && wine.id === item.wineId);
        if (wineDetail) {
          item.wine = wineDetail;
          this.loadWineImage(item);
        }
      });
      this.isLoading = false;
    }).catch(error => {
      console.error('Error al cargar detalles de los vinos:', error);
      this.isLoading = false;
    });
  }

  loadWineImage(item: ExtendedOrderItemDto): void {
    const fileId = item.wine?.fileAttachmentId;
    if (fileId) {
      this.storageService.getImage(fileId, 100, 200).subscribe({
        next: (blob) => {
          item.wine!.imageUrl = URL.createObjectURL(blob);
        },
        error: () => {
          item.wine!.imageUrl = 'assets/images/no-image.png';
        }
      });
    } else {
      item.wine!.imageUrl = 'assets/images/no-image.png';
    }
  }

  goBack(): void {
    this.location.back();
  }

  getOrderStatusString(status: number | undefined): string {
    if (status === undefined) return 'Unknown';
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Paid';
      case 2: return 'Shipped';
      case 3: return 'Delivered';
      case 4: return 'Cancelled';
      default: return 'Unknown';
    }
  }
}
